import type { AlertSeverity, AlertStatus } from '@core/api/complianceTypes';
import type { PillTone } from '@shared/ui';

/**
 * When an alert is due, in the words the console leads with (display plan 1.5, section 3, veto item 2). The
 * severity rules of the compliance plan (1.1) as clocks, computed here and never stored:
 *
 * - **P1** "Today": the owner is told the same day; due 24 hours after the store received it.
 * - **P2** "Within 2 business days": due two UTC weekdays after receipt at the same time of day; a weekend receipt
 *   starts the clock on Monday 00:00 UTC. No holiday calendar.
 * - **P3** "This week": due 7 days after receipt.
 *
 * The clock runs from `receivedAt` (when the firm had the alert), keeps running while the alert is in hand, and
 * stops when it is done. Past the deadline the state reads "Overdue by N h" under a day, then "Overdue by N d".
 */
export interface DueState {
  kind: 'due' | 'overdue' | 'done';
  label: string;
  tone: PillTone;
  /** The severity code the words replace, kept for the hover and the drawer. */
  code: 'P1' | 'P2' | 'P3';
  /** ISO time of the deadline. */
  deadline: string;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const DUE_LABEL: Record<AlertSeverity, string> = { p1: 'Today', p2: 'Within 2 business days', p3: 'This week' };
const DUE_TONE: Record<AlertSeverity, PillTone> = { p1: 'loss', p2: 'warning', p3: 'neutral' };

/** The deadline of an alert received at `receivedAt`, by its severity. */
export function deadlineOf(severity: AlertSeverity, receivedAt: string): Date {
  const start = new Date(receivedAt);
  if (severity === 'p1') return new Date(start.getTime() + DAY);
  if (severity === 'p3') return new Date(start.getTime() + 7 * DAY);
  let t = start;
  if (isWeekend(t)) {
    while (isWeekend(t)) t = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate() + 1));
  }
  let added = 0;
  while (added < 2) {
    t = new Date(t.getTime() + DAY);
    if (!isWeekend(t)) added++;
  }
  return t;
}

function isWeekend(t: Date): boolean {
  const d = t.getUTCDay();
  return d === 0 || d === 6;
}

export function dueState(alert: { severity: AlertSeverity; status: AlertStatus; receivedAt: string }, now: Date = new Date()): DueState {
  const code = alert.severity.toUpperCase() as DueState['code'];
  const deadline = deadlineOf(alert.severity, alert.receivedAt);
  const iso = deadline.toISOString();
  if (alert.status === 'resolved') return { kind: 'done', label: 'Done', tone: 'gain', code, deadline: iso };
  const late = now.getTime() - deadline.getTime();
  if (late > 0) {
    const label = late < DAY ? `Overdue by ${Math.max(1, Math.floor(late / HOUR))} h` : `Overdue by ${Math.floor(late / DAY)} d`;
    return { kind: 'overdue', label, tone: 'loss', code, deadline: iso };
  }
  return { kind: 'due', label: DUE_LABEL[alert.severity], tone: DUE_TONE[alert.severity], code, deadline: iso };
}

/** How long an alert has been open, in the largest whole unit ("40 m", "4 h", "2 d"). */
export function openFor(receivedAt: string, now: Date = new Date()): string {
  const age = now.getTime() - Date.parse(receivedAt);
  if (age < MINUTE) return 'under 1 m';
  if (age < HOUR) return `${Math.floor(age / MINUTE)} m`;
  if (age < DAY) return `${Math.floor(age / HOUR)} h`;
  return `${Math.floor(age / DAY)} d`;
}

/** Queue order (display plan 2.2): overdue first, then P1, P2, P3, the oldest first within a band. */
export function byDue<T extends { severity: AlertSeverity; status: AlertStatus; receivedAt: string }>(now: Date = new Date()) {
  const band = (a: T) => (dueState(a, now).kind === 'overdue' ? 0 : a.severity === 'p1' ? 1 : a.severity === 'p2' ? 2 : 3);
  return (a: T, b: T) => band(a) - band(b) || Date.parse(a.receivedAt) - Date.parse(b.receivedAt);
}
